import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, DatePicker } from 'antd';
import { ArrowRightOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import dayjs from '@/utils/dayjsConfig';
import type { AiCallVo, BulkyRemovalFormData } from '@/facilities/modules/aiCall';
import {
  addressLabel,
  formatTs,
  byOperator,
  isTransferred,
  missingBulkyFields,
  transferLabel,
} from './utils';
import { LiveDot } from './LiveBits';
import { isLive, useElapsed } from './liveUtils';
import tptcLogo from '@/assets/images/tptc_logo.png';
import './iems.css';

export interface BulkyRemovalFormProps {
  call: AiCallVo;
  submitting?: boolean;
  onSubmit: (formData: BulkyRemovalFormData) => void;
  onDismiss: () => void;
}

const COLLECTION_WINDOW = '(1 PM  - 5 PM )';
const FLASH_MS = 1800;

const changedFields = (a: BulkyRemovalFormData, b: BulkyRemovalFormData): string[] => {
  const keys: string[] = [];
  const scalar = [
    'fullName',
    'email',
    'mobile',
    'telephone',
    'floor',
    'unit',
    'appointmentDate',
    'remarks',
    'termsAccepted',
  ] as const;
  scalar.forEach((k) => {
    if ((a[k] ?? '') !== (b[k] ?? '')) keys.push(k);
  });
  if (addressLabel(a.address) !== addressLabel(b.address)) keys.push('address');
  const ai = a.items || [];
  const bi = b.items || [];
  if (ai.length !== bi.length) keys.push('numItems');
  bi.forEach((item, i) => {
    if (ai[i] !== item) keys.push(`items.${i}`);
  });
  return keys;
};
const DATE_FMT = 'DD-MM-YYYY';
const REMARKS_MAX = 2000;

const normalise = (
  form: BulkyRemovalFormData | null | undefined,
): BulkyRemovalFormData => {
  const items = Array.isArray(form?.items) ? [...form!.items!] : [];
  return {
    fullName: form?.fullName ?? '',
    email: form?.email ?? '',
    mobile: form?.mobile ?? '',
    telephone: form?.telephone ?? '',
    address: form?.address ?? null,
    floor: form?.floor ?? '',
    unit: form?.unit ?? '',
    appointmentDate: form?.appointmentDate ?? null,
    items,
    numItems: items.length || form?.numItems || 0,
    remarks: form?.remarks ?? '',
    termsAccepted: form?.termsAccepted === true,
    complete: form?.complete,
    missingRequired: form?.missingRequired,
  };
};

/**
 * A faithful replica of the official IEMS "Bulky Items Removal Request Form" (same markup order,
 * texts, colours and dimensions as the live portal; its "Addess" typo is corrected here), pre-filled by the
 * AI agent. Nothing here talks to the real portal: Submit hands the data back to TMS.
 */
export default function BulkyRemovalForm({
  call,
  submitting,
  onSubmit,
  onDismiss,
}: BulkyRemovalFormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<BulkyRemovalFormData>(() => normalise(call.formData));
  const [captcha, setCaptcha] = useState(false);
  const live = isLive(call);
  const elapsed = useElapsed(call.startedAt || call.createdAt, live);

  // fields the agent changed since the previous live snapshot flash briefly
  const [flash, setFlash] = useState<Set<string>>(() => new Set());
  const prevRef = useRef<{ id: number; form: BulkyRemovalFormData } | null>(null);

  useEffect(() => {
    const next = normalise(call.formData);
    const prev = prevRef.current;
    prevRef.current = { id: call.id, form: next };
    setForm(next);
    setCaptcha(false);
    if (!live || !prev || prev.id !== call.id) return undefined;
    const changed = changedFields(prev.form, next);
    if (changed.length === 0) return undefined;
    setFlash(new Set(changed));
    const timer = window.setTimeout(() => setFlash(new Set()), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [call.id, call.formData, live]);

  const fx = (key: string) => (flash.has(key) ? 'tcwf-flash' : undefined);

  const readOnly = call.status !== 'PENDING_REVIEW';
  const missing = useMemo(() => missingBulkyFields(form), [form]);
  const canSubmit = !readOnly && missing.length === 0 && captcha && !submitting;

  const set = <K extends keyof BulkyRemovalFormData>(
    key: K,
    value: BulkyRemovalFormData[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const setItemCount = (n: number) => {
    const items = [...(form.items || [])].slice(0, n);
    while (items.length < n) items.push('');
    setForm((prev) => ({ ...prev, items, numItems: n }));
  };

  const setItem = (i: number, value: string) => {
    const items = [...(form.items || [])];
    items[i] = value;
    setForm((prev) => ({ ...prev, items }));
  };

  const numItems = form.items?.length || 0;

  return (
    <div className="pb-8">
      {/* operator toolbar (outside the mock portal page) */}
      {live ? (
        <div className="live-banner" data-testid="live-banner">
          <LiveDot />
          <span className="live-banner__title">{t('hotline_inProgress')}</span>
          <span>{t('hotline_liveFilling')}</span>
          <span className="live-banner__time">{elapsed}</span>
        </div>
      ) : (
        <div className="max-w-[889px] mx-auto mb-3 flex items-center justify-between gap-3">
          <div className="text-[13px] text-[#00000099] dark:text-[#ffffff99]">
            {call.status === 'PENDING_REVIEW'
              ? t('hotline_reviewHint')
              : call.status === 'SUBMITTED'
                ? `${t('hotline_submittedBy')} ${call.reviewedByName || '-'} · ${formatTs(call.reviewedAt)}`
                : `${t('hotline_dismissedBy')} ${call.reviewedByName || '-'} · ${formatTs(call.reviewedAt)}`}
          </div>
          {!readOnly && (
            <Button danger type="text" onClick={onDismiss} disabled={submitting}>
              {t('hotline_dismiss')}
            </Button>
          )}
        </div>
      )}

      {call.status === 'SUBMITTED' && (
        <Alert
          className="max-w-[889px] mx-auto mb-3"
          type="success"
          showIcon
          message={`${t('hotline_caseCreated')}: ${call.caseNo || call.caseId}${call.caseStatus ? ` · ${t(call.caseStatus)}` : ''}`}
          description={t('hotline_caseCreatedDesc')}
        />
      )}
      {call.status === 'DISMISSED' && (
        <Alert
          className="max-w-[889px] mx-auto mb-3"
          type="info"
          showIcon
          message={t('hotline_dismissedInfo')}
          description={call.reviewNote}
        />
      )}
      {!live && isTransferred(call) && (
        <Alert
          className="max-w-[889px] mx-auto mb-3"
          type="info"
          showIcon
          message={transferLabel(call, t)}
          description={t(
            byOperator(call) ? 'hotline_takenOverDesc' : 'hotline_transferredDesc',
          )}
        />
      )}
      {!readOnly && !live && form.complete === false && (
        <Alert
          className="max-w-[889px] mx-auto mb-3"
          type="warning"
          showIcon
          message={t('hotline_incompleteTitle')}
          description={`${t('hotline_incompleteDesc')} ${missing.map((m) => t(`hotline_field_${m}`)).join(', ')}`}
        />
      )}

      <div className="tcwf-page">
        <div className="tcwf-card" data-testid="iems-form">
          <div className="tcwf-header">
            <img className="tcwf-logo" src={tptcLogo} alt="Tanjong Pagar Town Council" />
            <div className="tcwf-title">Bulky Items Removal Request Form</div>
          </div>

          <div className="tcwf-form">
            <label className="tcwf-required">
              <span className="tcwf-red">* </span>Required
            </label>

            <Section title="Requestor Particulars" />

            <div className="tcwf-fields">
              <div>
                <label
                  className="tcwf-label tcwf-label--mandatory"
                  htmlFor="tcwf-fullname"
                >
                  Full Name
                </label>
                <input
                  id="tcwf-fullname"
                  className={clsx('tcwf-input', fx('fullName'))}
                  value={form.fullName ?? ''}
                  placeholder="Full Name"
                  maxLength={50}
                  disabled={readOnly}
                  onChange={(e) => set('fullName', e.target.value)}
                />
              </div>
              <div>
                <label className="tcwf-label" htmlFor="tcwf-email">
                  Email
                </label>
                <input
                  id="tcwf-email"
                  className={clsx('tcwf-input', fx('email'))}
                  value={form.email ?? ''}
                  placeholder="email@domain.com"
                  maxLength={50}
                  disabled={readOnly}
                  onChange={(e) => set('email', e.target.value)}
                />
              </div>
              <div>
                <label className="tcwf-label tcwf-label--mandatory" htmlFor="tcwf-mobile">
                  Mobile Number
                </label>
                <input
                  id="tcwf-mobile"
                  className={clsx('tcwf-input', fx('mobile'))}
                  value={form.mobile ?? ''}
                  placeholder="91234567"
                  maxLength={8}
                  disabled={readOnly}
                  onChange={(e) => set('mobile', e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div>
                <label className="tcwf-label" htmlFor="tcwf-telephone">
                  Telephone Number
                </label>
                <input
                  id="tcwf-telephone"
                  className={clsx('tcwf-input', fx('telephone'))}
                  value={form.telephone ?? ''}
                  placeholder="61234567"
                  maxLength={8}
                  disabled={readOnly}
                  onChange={(e) => set('telephone', e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div>
                <label className="tcwf-label" htmlFor="tcwf-address">
                  Address&nbsp; <span className="tcwf-red">*</span>
                </label>
                <div className="tcwf-select-wrap">
                  <input
                    id="tcwf-address"
                    className={clsx('tcwf-input tcwf-input--select', fx('address'))}
                    value={addressLabel(form.address)}
                    placeholder="Type your address and select from the dropdown"
                    disabled={readOnly}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        set('address', null);
                        return;
                      }
                      const current = form.address;
                      if (current && value === addressLabel(current)) return;
                      set('address', {
                        ...(current || {}),
                        block: undefined,
                        street: undefined,
                        full: value,
                      });
                    }}
                  />
                  <span className="tcwf-chevron" aria-hidden />
                </div>
              </div>
              <div className="tcwf-grid2">
                <div className="tcwf-col6">
                  <label
                    className="tcwf-label tcwf-label--mandatory"
                    htmlFor="tcwf-floor"
                  >
                    Floor
                  </label>
                  <div className="tcwf-icon-input">
                    <span className="tcwf-icon-input__icon">#</span>
                    <input
                      id="tcwf-floor"
                      className={clsx('tcwf-input tcwf-input--icon', fx('floor'))}
                      value={form.floor ?? ''}
                      placeholder="01"
                      maxLength={4}
                      disabled={readOnly}
                      onChange={(e) => set('floor', e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                </div>
                <div className="tcwf-col6 tcwf-col6--gutter">
                  <label className="tcwf-label tcwf-label--mandatory" htmlFor="tcwf-unit">
                    Unit
                  </label>
                  <div className="tcwf-icon-input">
                    <span className="tcwf-icon-input__icon">-</span>
                    <input
                      id="tcwf-unit"
                      className={clsx('tcwf-input tcwf-input--icon', fx('unit'))}
                      value={form.unit ?? ''}
                      placeholder="01"
                      maxLength={4}
                      disabled={readOnly}
                      onChange={(e) => set('unit', e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Section title="Request Details" />

            <div className="tcwf-fields">
              <div>
                <label className="tcwf-label tcwf-label--mandatory">
                  Appointment Date
                </label>
                <div className="tcwf-date-row">
                  <DatePicker
                    className={clsx(
                      'tcwf-date',
                      fx('appointmentDate') && 'tcwf-flash-box',
                    )}
                    format={DATE_FMT}
                    placeholder="dd-MM-yyyy"
                    suffixIcon={null}
                    allowClear={false}
                    disabled={readOnly}
                    value={form.appointmentDate ? dayjs(form.appointmentDate) : null}
                    disabledDate={(d) =>
                      d.day() === 0 || d.day() === 6 || d.isBefore(dayjs().startOf('day'))
                    }
                    onChange={(d) =>
                      set('appointmentDate', d ? d.format('YYYY-MM-DD') : null)
                    }
                  />
                  <span className="tcwf-slot">{COLLECTION_WINDOW}</span>
                </div>
              </div>

              <div className="tcwf-notes">
                <span className="tcwf-note-italic">
                  Appointment date to be a minimum of 3 working days in advance.
                </span>
                <div className="tcwf-note-body">
                  {[
                    'This service is completely free and is only available to residents staying in HDB units within Tanjong Pagar Town. It is not applicable to private residents or commercial tenants.',
                    'Residents may call us to arrange for an appointment at 1800 272 6415 from Mondays to Friday, 8am to 5pm.',
                    'Each appointment is limited to three (3) movable items per month, and appointments must be booked at least three (3) working days in advance.',
                    'The service does not apply to residents moving house, nor does it include the dismantling or removal of built-in furniture and renovation debris.',
                  ].join('\n\n')}
                </div>
              </div>

              <div className="tcwf-item-row">
                <label
                  className="tcwf-label tcwf-label--mandatory tcwf-item-label"
                  htmlFor="tcwf-count"
                >
                  No. of bulky items:
                </label>
                <div className="tcwf-select-wrap tcwf-select-wrap--count">
                  <select
                    id="tcwf-count"
                    className={clsx('tcwf-dropdown', fx('numItems'))}
                    value={numItems || -1}
                    disabled={readOnly}
                    onChange={(e) =>
                      setItemCount(
                        Number(e.target.value) > 0 ? Number(e.target.value) : 0,
                      )
                    }
                  >
                    <option value={-1}>Select</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                  </select>
                  <span className="tcwf-chevron" aria-hidden />
                </div>
              </div>
              {Array.from({ length: numItems }).map((_, i) => (
                <div className="tcwf-item-row" key={i}>
                  <label
                    className="tcwf-label tcwf-label--mandatory tcwf-item-label"
                    htmlFor={`tcwf-item-${i}`}
                  >
                    Bulky item {i + 1}:
                  </label>
                  <input
                    id={`tcwf-item-${i}`}
                    className={clsx('tcwf-input tcwf-input--item', fx(`items.${i}`))}
                    value={form.items?.[i] ?? ''}
                    placeholder="bulky item"
                    maxLength={50}
                    disabled={readOnly}
                    onChange={(e) => setItem(i, e.target.value)}
                  />
                  <div
                    className="tcwf-upload"
                    title="File upload is not available in TMS"
                  >
                    <PaperClipOutlined className="tcwf-upload__icon" />
                    <span>Choose file</span>
                  </div>
                </div>
              ))}
              {numItems > 0 && (
                <div className="tcwf-formats">
                  Accepted formats: png, jpeg, jpg, pdf. Combined file size can not more
                  than 5MB.
                </div>
              )}

              <div>
                <label className="tcwf-label" htmlFor="tcwf-remarks">
                  Remarks/ Description of item(s) to be removed
                </label>
                <textarea
                  id="tcwf-remarks"
                  className={clsx('tcwf-textarea', fx('remarks'))}
                  rows={3}
                  maxLength={REMARKS_MAX}
                  value={form.remarks ?? ''}
                  disabled={readOnly}
                  onChange={(e) => set('remarks', e.target.value)}
                />
                <div className="tcwf-counter">
                  {REMARKS_MAX - (form.remarks?.length || 0)}
                </div>
              </div>
            </div>

            <Section title="Terms and Conditions" />

            <div className="tcwf-tc">
              <div className="tcwf-tc__text">
                <p>
                  Each appointment is limited to three (3) movable items per month, and
                  appointments must be booked at least three (3) working days in advance.
                  The service does not apply to residents moving house, nor does it
                  include the dismantling or removal of built-in furniture and renovation
                  debris.
                </p>
              </div>
              <div className="tcwf-agree">
                {/* the live portal shows the checkbox without a caption */}
                <input
                  type="checkbox"
                  className={clsx(
                    'tcwf-checkbox',
                    fx('termsAccepted') && 'tcwf-flash-box',
                  )}
                  checked={form.termsAccepted === true}
                  disabled={readOnly}
                  title={t('hotline_termsLabel')}
                  aria-label={t('hotline_termsLabel')}
                  data-testid="iems-terms"
                  onChange={(e) => set('termsAccepted', e.target.checked)}
                />
              </div>
            </div>

            <div className="tcwf-captcha-wrap">
              <div
                className="tcwf-captcha"
                role="checkbox"
                tabIndex={readOnly ? -1 : 0}
                aria-checked={captcha || (readOnly && !live)}
                aria-disabled={readOnly}
                aria-label="I'm not a robot"
                data-testid="iems-captcha"
                onClick={() => !readOnly && setCaptcha((v) => !v)}
                onKeyDown={(e) => {
                  if (!readOnly && (e.key === ' ' || e.key === 'Enter')) {
                    e.preventDefault();
                    setCaptcha((v) => !v);
                  }
                }}
              >
                <span
                  className={`tcwf-captcha__box${captcha || (readOnly && !live) ? ' is-checked' : ''}`}
                />
                <span className="tcwf-captcha__text">I'm not a robot</span>
                <span className="tcwf-captcha__brand">
                  <span className="tcwf-captcha__logo" />
                  reCAPTCHA
                  <small>Privacy - Terms</small>
                </span>
              </div>
            </div>

            <div className="tcwf-submit-wrap">
              <button
                type="button"
                className="tcwf-submit"
                disabled={!canSubmit}
                data-testid="iems-submit"
                onClick={() => onSubmit({ ...form, numItems: form.items?.length || 0 })}
              >
                <span>{submitting ? 'Submitting…' : 'Submit'}</span>
                <ArrowRightOutlined className="tcwf-submit__icon" />
              </button>
              <div className="tcwf-print">
                You can print a copy after you have submitted
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title }: { title: string }) {
  return (
    <div className="tcwf-section">
      <span className="tcwf-section__inner">
        <span className="tcwf-section__dot" />
        <span className="tcwf-section__text">{title}</span>
      </span>
    </div>
  );
}
